import { useEffect, useState } from 'react';
import { 
  collection, 
  doc, 
  onSnapshot, 
  setDoc, 
  updateDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../../../index';
import { GameSession, GameState, Player, Submission, TeamStats, TeamColor } from '../types';

export function useGameSession(sessionId: string) {
  const [session, setSession] = useState<GameSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!sessionId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'battleRoyale', sessionId),
      (doc) => {
        if (doc.exists()) {
          setSession({ id: doc.id, ...doc.data() } as GameSession);
        } else {
          setError(new Error('Session not found'));
        }
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [sessionId]);

  return { session, loading, error };
}

export function usePlayer(sessionId: string, playerId: string) {
  const [player, setPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId || !playerId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'battleRoyale', sessionId, 'players', playerId),
      (doc) => {
        if (doc.exists()) {
          setPlayer({ id: doc.id, ...doc.data() } as Player);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [sessionId, playerId]);

  return { player, loading };
}

export function useTeamStats(sessionId: string) {
  const [stats, setStats] = useState<TeamStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) return;

    // プレイヤーコレクションを監視してチームスコアを計算
    const unsubscribe = onSnapshot(
      collection(db, 'battleRoyale', sessionId, 'players'),
      async (snapshot) => {
        const players = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Player));

        // チームごとにスコアを集計
        const teamScoreMap: Record<TeamColor, { totalScore: number; playerCount: number }> = {
          [TeamColor.RED]: { totalScore: 0, playerCount: 0 },
          [TeamColor.BLUE]: { totalScore: 0, playerCount: 0 },
          [TeamColor.GREEN]: { totalScore: 0, playerCount: 0 },
          [TeamColor.YELLOW]: { totalScore: 0, playerCount: 0 },
          [TeamColor.PURPLE]: { totalScore: 0, playerCount: 0 },
          [TeamColor.ORANGE]: { totalScore: 0, playerCount: 0 }
        };

        players.forEach(player => {
          if (player.teamColor && teamScoreMap[player.teamColor]) {
            const score = player.answeredIds ? player.answeredIds.length : (player.score || 0);
            teamScoreMap[player.teamColor].totalScore += score;
            teamScoreMap[player.teamColor].playerCount += 1;
          }
        });

        // 合計スコアを計算
        const totalScore = Object.values(teamScoreMap).reduce(
          (sum, team) => sum + team.totalScore, 
          0
        );

        // TeamStats形式に変換
        const teamStats: TeamStats[] = Object.entries(teamScoreMap).map(([color, data]) => ({
          teamColor: color as TeamColor,
          totalScore: data.totalScore,
          scorePercentage: totalScore > 0 ? (data.totalScore / totalScore) * 100 : 0,
          playerCount: data.playerCount
        }));

        // スコア降順でソート
        teamStats.sort((a, b) => b.totalScore - a.totalScore);
        
        // デバッグ用ログ
        console.log('Team Stats Calculation:', {
          totalScore,
          teamStats: teamStats.map(t => ({
            color: t.teamColor,
            score: t.totalScore,
            percentage: t.scorePercentage.toFixed(2) + '%'
          }))
        });
        
        setStats(teamStats);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching team stats:', error);
        setStats([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [sessionId]);

  return { stats, loading };
}

export function useLatestSubmissions(sessionId: string, limitCount = 10) {
  const [submissions, setSubmissions] = useState<Submission[]>([]);

  useEffect(() => {
    if (!sessionId) return;

    const q = query(
      collection(db, 'battleRoyale', sessionId, 'submissions'),
      orderBy('submittedAt', 'desc'),
      limit(limitCount)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const subs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Submission));
      setSubmissions(subs);
    });

    return () => unsubscribe();
  }, [sessionId, limitCount]);

  return submissions;
}